import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Clock, RefreshCw, CalendarDays } from 'lucide-react';
import { useSignageSchedules, type Schedule, type CreateScheduleInput } from '@/hooks/signage';
import { ScheduleCard } from './ScheduleCard';
import { CreateScheduleDialog } from './CreateScheduleDialog';
import { ScheduleCalendar } from './ScheduleCalendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface ScheduleListProps {
  businessId: string;
}

export function ScheduleList({ businessId }: ScheduleListProps) {
  const {
    schedules,
    loading,
    fetchSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    toggleScheduleActive,
    duplicateSchedule,
  } = useSignageSchedules(businessId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [view, setView] = useState<'list' | 'calendar'>('list');

  const handleCreate = async (data: CreateScheduleInput) => {
    await createSchedule(data);
  };

  const handleEdit = async (data: CreateScheduleInput) => {
    if (editingSchedule) {
      await updateSchedule(editingSchedule.id, data);
      setEditingSchedule(null);
    }
  };

  const activeSchedules = schedules.filter(s => s.is_active);
  const inactiveSchedules = schedules.filter(s => !s.is_active);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-10 w-32 bg-muted animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-32 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Content Schedules</h2>
          <p className="text-sm text-muted-foreground">
            {schedules.length} schedule{schedules.length !== 1 ? 's' : ''} configured
            {activeSchedules.length > 0 && ` (${activeSchedules.length} active)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchSchedules}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Schedule
          </Button>
        </div>
      </div>

      {/* View Toggle */}
      <Tabs value={view} onValueChange={(v) => setView(v as any)}>
        <TabsList>
          <TabsTrigger value="list">
            <Clock className="w-4 h-4 mr-2" />
            List View
          </TabsTrigger>
          <TabsTrigger value="calendar">
            <CalendarDays className="w-4 h-4 mr-2" />
            Calendar View
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          {schedules.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Clock className="w-8 h-8 text-muted-foreground" />
                </div>
                <CardTitle className="text-lg mb-2">No schedules yet</CardTitle>
                <CardDescription className="text-center max-w-sm mb-4">
                  Create schedules to automatically display different content at specific times and days.
                </CardDescription>
                <Button onClick={() => setIsCreateOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Schedule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Active Schedules */}
              {activeSchedules.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Active Schedules
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeSchedules.map((schedule) => (
                      <ScheduleCard
                        key={schedule.id}
                        schedule={schedule}
                        onEdit={() => setEditingSchedule(schedule)}
                        onDelete={() => deleteSchedule(schedule.id)}
                        onDuplicate={() => duplicateSchedule(schedule.id)}
                        onToggleActive={(active) =>
                          toggleScheduleActive(schedule.id, active)
                        }
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Inactive Schedules */}
              {inactiveSchedules.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Inactive Schedules
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {inactiveSchedules.map((schedule) => (
                      <ScheduleCard
                        key={schedule.id}
                        schedule={schedule}
                        onEdit={() => setEditingSchedule(schedule)}
                        onDelete={() => deleteSchedule(schedule.id)}
                        onDuplicate={() => duplicateSchedule(schedule.id)}
                        onToggleActive={(active) =>
                          toggleScheduleActive(schedule.id, active)
                        }
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <ScheduleCalendar
            schedules={schedules}
            onScheduleClick={(schedule) => setEditingSchedule(schedule)}
          />
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <CreateScheduleDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        businessId={businessId}
        onSubmit={handleCreate}
      />

      {/* Edit Dialog */}
      <CreateScheduleDialog
        open={!!editingSchedule}
        onOpenChange={(open) => !open && setEditingSchedule(null)}
        businessId={businessId}
        schedule={editingSchedule}
        onSubmit={handleEdit}
      />
    </div>
  );
}
